import React, { useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Grid,
    Avatar,
    Button,
    Typography,
    Tooltip,
    IconButton,
    Chip,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SettingsIcon from '@material-ui/icons/Settings';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { generateUrlForWorkspace } from '../common';
import { CountryFlag } from '../CountryFlag';
import IssuesPanel from './IssuesPanel';

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
`;

const CardRoot = styled(Card)`
    position: relative;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 12px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: none !important;
    transition: box-shadow 150ms ease, transform 150ms ease !important;
    &:hover {
        box-shadow: 0 4px 16px rgba(0,0,0,.09) !important;
        transform: translateY(-2px);
    }
    @media (max-width: 600px) {
        border-radius: 0 !important;
        border-left: none !important;
        border-right: none !important;
        &:hover { transform: none; }
    }
`;

const Content = styled(CardContent)`
    padding: 16px !important;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: 52px 1fr;
    gap: 12px;
    align-items: center;
`;

const LogoAvatar = styled(Avatar)`
    && {
        background-color: #f3f4f6 !important;
        color: #374151 !important;
        font-weight: 600 !important;
        font-size: 1rem !important;
    }
    width: 52px !important;
    height: 52px !important;
    border: 2px solid #e5e7eb !important;
    box-shadow: 0 1px 4px rgba(0,0,0,.1) !important;
    overflow: hidden;
    position: relative !important;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FaviconFit = styled.div`
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    & img {
        width: 70%;
        height: 70%;
        object-fit: contain;
    }
`;

const TextCol = styled.div`
    min-width: 0;
`;

const NameRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const NameText = styled(Typography)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600 !important;
    flex: 1 1 auto;
    min-width: 0;
`;

const FlagInline = styled.span`
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    & img, & svg {
        height: 16px;
        width: auto;
        border-radius: 2px;
    }
`;

const DomainLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    color: #6b7280;
    text-decoration: none;
    font-size: 0.8rem;
    &:hover { color: #4f46e5; text-decoration: underline; }
`;

const DomainEllipsis = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 auto;
    min-width: 0;
`;

const MetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const StatusDot = styled(({ $color, $animate, ...props }) => <FiberManualRecordIcon {...props} />)`
    && {
        font-size: 10px !important;
        color: ${({ $color }) => $color};
        animation: ${({ $animate }) => ($animate ? pulse : 'none')} 1.4s ease-in-out infinite;
    }
`;

const StatusChip = styled(({ $bg, $fg, ...props }) => <Chip {...props} />)`
    && {
        height: 22px;
        font-size: 0.7rem;
        font-weight: 500;
        background-color: ${({ $bg }) => $bg};
        color: ${({ $fg }) => $fg};
        border-radius: 6px !important;
    }
    && .MuiChip-label { padding: 0 8px; }
`;

const Actions = styled(CardActions)`
    padding: 10px 16px 14px !important;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OpenButton = styled(Button)`
    && {
        background: linear-gradient(135deg, #6366f1, #8b5cf6);
        color: #fff;
        font-weight: 600;
        border-radius: 8px;
        padding: 6px 20px;
        text-transform: none;
        font-size: 0.875rem;
        box-shadow: none;
    }
    &&:hover {
        background: linear-gradient(135deg, #4f46e5, #7c3aed);
        box-shadow: 0 4px 12px rgba(99,102,241,.35);
    }
`;

const RightActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
`;

function initials(name) {
    const parts = (name || 'Workspace').trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase()).join('') || 'WS';
}

function safeHref(url) {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function getDomain(url) {
    try {
        const href = safeHref(url);
        if (!href) return '';
        return new URL(href).host.replace(/^www\./, '');
    } catch {
        return '';
    }
}

function faviconCandidates(url) {
    try {
        const href = safeHref(url);
        if (!href) return [];
        const u = new URL(href);
        const domain = u.hostname.replace(/^www\./, '');
        return [
            `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
            `https://icons.duckduckgo.com/ip3/${domain}.ico`,
            `${u.origin}/favicon.ico`,
        ];
    } catch {
        return [];
    }
}

const WorkspaceCard = ({ workspace, loadingIssues, issueCounts }) => {
    const name = workspace?.name || 'Unnamed Workspace';
    const website = workspace?.websiteUrl || '';
    const countryCode = workspace?.countryCode || '';
    const crawlEnabled = workspace?.crawlerSettings?.crawlEnabled ?? false;
    const isCrawling = !!workspace?.crawlStarting;
    const crawlCount = Array.isArray(workspace?.lastCrawls) ? workspace.lastCrawls.length : 0;

    const href = useMemo(() => safeHref(website), [website]);
    const domain = useMemo(() => getDomain(website), [website]);
    const candidates = useMemo(() => faviconCandidates(website), [website]);
    const [iconIdx, setIconIdx] = useState(0);
    const iconSrc = candidates[iconIdx];

    const openUrl = generateUrlForWorkspace(workspace.seqNumber);
    const adminUrl = `${openUrl}/admin`;

    let crawlerLabel, crawlerBg, crawlerFg;
    if (isCrawling) {
        crawlerLabel = 'Crawling…';
        crawlerBg = '#fef3c7';
        crawlerFg = '#92400e';
    } else if (crawlEnabled) {
        crawlerLabel = 'Crawler on';
        crawlerBg = '#d1fae5';
        crawlerFg = '#065f46';
    } else {
        crawlerLabel = 'Crawler off';
        crawlerBg = '#f3f4f6';
        crawlerFg = '#6b7280';
    }

    return (
        <Grid item xs={12} sm={6} md={4}>
            <CardRoot>
                <Content>
                    <HeaderRow>
                        <LogoAvatar aria-label={`${name} logo`}>
                            {iconSrc ? (
                                <FaviconFit>
                                    <img
                                        src={iconSrc}
                                        alt=""
                                        draggable={false}
                                        onError={() => setIconIdx((i) => i + 1)}
                                    />
                                </FaviconFit>
                            ) : (
                                initials(name)
                            )}
                        </LogoAvatar>

                        <TextCol>
                            <NameRow>
                                {countryCode && (
                                    <FlagInline title={countryCode}>
                                        <CountryFlag countryCode={countryCode} />
                                    </FlagInline>
                                )}
                                <NameText variant="subtitle1" title={name}>
                                    {name}
                                </NameText>
                            </NameRow>
                            {domain ? (
                                <DomainLink href={href} target="_blank" rel="noopener noreferrer" title={href}>
                                    <DomainEllipsis>{domain}</DomainEllipsis>
                                    <OpenInNewIcon style={{ fontSize: 13, flexShrink: 0 }} />
                                </DomainLink>
                            ) : (
                                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>No website</span>
                            )}
                        </TextCol>
                    </HeaderRow>

                    <MetaRow>
                        <StatusChip
                            size="small"
                            $bg={crawlerBg}
                            $fg={crawlerFg}
                            icon={
                                <StatusDot
                                    $color={isCrawling ? '#f59e0b' : crawlEnabled ? '#10b981' : '#d1d5db'}
                                    $animate={isCrawling || undefined}
                                />
                            }
                            label={crawlerLabel}
                        />
                        {crawlCount > 0 && (
                            <Typography variant="caption" style={{ color: '#9ca3af' }}>
                                {crawlCount} crawl{crawlCount !== 1 ? 's' : ''}
                            </Typography>
                        )}
                    </MetaRow>
                    <IssuesPanel workspaceNumber={workspace.seqNumber} loading={loadingIssues} counts={issueCounts} />
                </Content>

                <Actions>
                    <OpenButton component={Link} to={openUrl} variant="contained" aria-label={`Open ${name}`}>
                        Open
                    </OpenButton>
                    <RightActions>
                        <Tooltip title="Workspace settings">
                            <IconButton
                                component={Link}
                                to={adminUrl}
                                aria-label={`Settings for ${name}`}
                                size="small"
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </RightActions>
                </Actions>
            </CardRoot>
        </Grid>
    );
};

export default WorkspaceCard;
